import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { FiCopy, FiTrash2 } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../context/ProjectContext';
import { API_URL, apiRequest } from '../config/api';

// Simple in-memory cache scoped to module lifetime
const apiKeysCacheByProjectId = {};

const ApiKeys = () => {
  const { t } = useTranslation();
  const { user, handleAuthError } = useAuth();
  const { selectedProject } = useProjects();
  const [keys, setKeys] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [scopeRead, setScopeRead] = useState(false);
  const [scopeWrite, setScopeWrite] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const hasHydratedFromCacheRef = useRef(false);
  const [createdKeyToken, setCreatedKeyToken] = useState('');

  const projectId = selectedProject?.id;
  const visibleKeys = Array.isArray(keys)
    ? keys.filter(({ status }) => String(status || '').toUpperCase() !== 'REVOKED')
    : [];

  const fetchKeys = async () => {
    if (!user?.token || !projectId) return;
    // If we have no data yet for this project, show loader; otherwise refresh silently
    const hasCached = Array.isArray(apiKeysCacheByProjectId[projectId]);
    if (!hasCached) {
      setIsLoading(true);
    }
    try {
      const response = await apiRequest(`${API_URL}/api/api/projects/${projectId}/api-keys`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, handleAuthError);
      if (!response.ok) {
        throw new Error('Failed to load API keys');
      }
      const data = await response.json();
      const normalized = Array.isArray(data) ? data : [];
      apiKeysCacheByProjectId[projectId] = normalized;
      setKeys(normalized);
    } catch (err) {
      console.error('Failed to load API keys', err);
      if (!hasCached) {
        setKeys([]);
      }
    } finally {
      if (!hasCached) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    // Hydrate from cache immediately if available
    if (projectId && Array.isArray(apiKeysCacheByProjectId[projectId])) {
      setKeys(apiKeysCacheByProjectId[projectId]);
      hasHydratedFromCacheRef.current = true;
    } else {
      setKeys([]);
      hasHydratedFromCacheRef.current = false;
    }
    // Always refresh in background
    fetchKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, user?.token]);

  const handleCreateKey = () => {
    setShowCreateModal(true);
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setNewKeyName('');
    setScopeRead(false);
    setScopeWrite(false);
  };

  const handleSubmitCreate = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) {
      toast.error(t('apiKeys.toasts.enterName'));
      return;
    }
    if (!user?.token) {
      toast.error('Not authenticated');
      return;
    }

    const scopes = [];
    if (scopeRead) scopes.push('read');
    if (scopeWrite) scopes.push('write');

    if (scopes.length === 0) {
      toast.error(t('apiKeys.toasts.selectScopes', { defaultValue: 'Select at least one scope' }));
      return;
    }

    if (!expiresAt) {
      toast.error('Expiration date/time is required');
      return;
    }

    const expiresAtIso = new Date(`${expiresAt}T00:00:00`).toISOString();

    const requestBody = {
      name: newKeyName.trim(),
      scopes,
      expiresAt: expiresAtIso,
    };

    try {
      setIsSubmitting(true);
      if (!projectId) {
        throw new Error('No project selected');
      }
      const response = await fetch(`${API_URL}/api/api/projects/${projectId}/api-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to create API key';
        try {
          const data = await response.json();
          errorMessage = data.message || data.error || errorMessage;
        } catch {}
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const tokenValue = data?.token || data?.key || data?.apiKey || data?.secret || '';
      // Refresh from backend to get server view (without exposing secret)
      await fetchKeys();
      setShowCreateModal(false);
      setNewKeyName('');
      setScopeRead(false);
      setScopeWrite(false);
      setExpiresAt('');
      if (tokenValue) {
        setCreatedKeyToken(tokenValue);
      }
      toast.success(t('apiKeys.toasts.createdSuccess'));
    } catch (err) {
      console.error('Failed to create API key', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteKey = (id) => {
    const keyToDelete = keys.find(key => key.id === id);
    if (!keyToDelete) return;
    if (!projectId || !user?.token) return;
    if (window.confirm(t('apiKeys.confirmations.deleteKey', { name: keyToDelete.name }))) {
      (async () => {
        try {
          const response = await fetch(`${API_URL}/api/api/projects/${projectId}/api-keys/${id}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${user.token}`,
              'Content-Type': 'application/json',
            },
          });
          if (!response.ok) {
            let errorMessage = 'Failed to delete API key';
            try {
              const data = await response.json();
              errorMessage = data.message || data.error || errorMessage;
            } catch {}
            throw new Error(errorMessage);
          }
          toast.success(t('apiKeys.toasts.deletedSuccess'));
          await fetchKeys();
        } catch (err) {
          console.error('Failed to delete API key', err);
        }
      })();
    }
  };

  const handleCopy = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
      toast.success(t('apiKeys.toasts.copiedSuccess'));
    } catch (err) {
      toast.error(t('apiKeys.toasts.copyFailed'));
    }
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">{t('apiKeys.title')}</h1>
        <button 
          onClick={handleCreateKey} 
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
        >
          {t('apiKeys.createApiKey')}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apiKeys.table.name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('apiKeys.table.token', { defaultValue: 'Token' })}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {/* intentionally left blank */}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <>
                {Array.from({ length: 8 }).map((_, idx) => (
                  <tr key={idx} className="animate-pulse">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-40" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="h-4 bg-gray-200 rounded w-64" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex space-x-2 justify-end">
                        <div className="w-4 h-4 bg-gray-200 rounded" />
                      </div>
                    </td>
                  </tr>
                ))}
              </>
            ) : visibleKeys.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-4 text-sm text-gray-500">No API keys</td>
              </tr>
            ) : visibleKeys.map(({ id, name, key, apiKey, secret, value, token, maskedKey }) => (
              <tr key={id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {(() => {
                    const displayedKey = token || key || apiKey || maskedKey || value || secret || '';
                    return (
                      <div className="flex items-center justify-between max-w-[360px]">
                        <span className="font-mono text-xs break-all pr-3 text-gray-800">{displayedKey || '—'}</span>
                        {displayedKey ? (
                          <button
                            onClick={() => handleCopy(displayedKey)}
                            title={t('apiKeys.actions.copyToClipboard')}
                            className="p-1 text-gray-400 hover:text-gray-700 transition-colors duration-200"
                          >
                            <FiCopy size={16} />
                          </button>
                        ) : null}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 w-full">
                  <div className="flex items-center justify-end">
                    <button
                      onClick={() => handleDeleteKey(id)}
                      title={t('apiKeys.actions.deleteApiKey')}
                      className="p-1 text-gray-400 hover:text-red-600 transition-colors duration-200"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
                 </table>
       </div>

       {/* Create API Key Modal */}
       {showCreateModal && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
             <div className="mt-3">
               <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('apiKeys.modal.title')}
               </h3>
               <form onSubmit={handleSubmitCreate}>
                 <div className="mb-6">
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('apiKeys.modal.name')}
                   </label>
                   <input
                     type="text"
                     value={newKeyName}
                     onChange={(e) => setNewKeyName(e.target.value)}
                     placeholder={t('apiKeys.modal.namePlaceholder')}
                     className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                 </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('apiKeys.modal.scopes', { defaultValue: 'Scopes' })}
                  </label>
                  <div className="w-full border border-gray-300 rounded-md px-3 py-2 bg-white flex flex-col space-y-2">
                    <label className="inline-flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={scopeRead}
                        onChange={(e) => setScopeRead(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{t('apiKeys.scopes.read', { defaultValue: 'read' })}</span>
                    </label>
                    <label className="inline-flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={scopeWrite}
                        onChange={(e) => setScopeWrite(e.target.checked)}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{t('apiKeys.scopes.write', { defaultValue: 'write' })}</span>
                    </label>
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Expires At
                  </label>
                  <input
                    type="date"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                 <div className="flex justify-end space-x-3">
                   <button
                     type="button"
                     onClick={handleCloseModal}
                     className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors duration-200"
                   >
                     {t('common.cancel')}
                   </button>
                   <button
                     type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-md transition-colors duration-200"
                   >
                    {isSubmitting ? 'Creating…' : t('apiKeys.modal.createApiKey')}
                   </button>
                 </div>
               </form>
             </div>
           </div>
         </div>
       )}

      {/* Token Display Modal */}
      {createdKeyToken && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
           <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
             <div className="mt-3">
               <h3 className="text-lg font-medium text-gray-900 mb-2">
                {t('apiKeys.modal.yourNewToken', { defaultValue: 'Your new API token' })}
               </h3>
               <div className="flex items-center justify-between border border-gray-200 rounded-md px-3 py-2 bg-gray-50">
                <span className="font-mono text-sm break-all pr-3 text-gray-800">{createdKeyToken}</span>
                 <button
                  onClick={() => handleCopy(createdKeyToken)}
                   title={t('apiKeys.actions.copyToClipboard')}
                   className="p-2 text-gray-500 hover:text-gray-700 transition-colors duration-200"
                 >
                   <FiCopy size={18} />
                 </button>
               </div>
               <div className="flex justify-end mt-4">
                 <button
                   type="button"
                  onClick={() => setCreatedKeyToken('')}
                   className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
                 >
                   {t('common.close', { defaultValue: 'Close' })}
                 </button>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 }

export default ApiKeys;